/**
 * 표 폭 — Lexical 노드와 닿는 쪽. 계산은 tableWidth.ts 가 하고, 여기는 노드를 읽고 쓰고 DOM 에 입힌다.
 *
 *   $readTableCells        표 노드 → 줄별 칸(colSpan·rowSpan·width·노드)
 *   $styleEditorTables     편집기 DOM 에 폭 계획을 입힌다(TableWidthPlugin 이 부른다)
 *   $fillMissingCellWidths 크기가 정해진 표에 폭 없는 칸이 생겼을 때 채운다(열 넣기)
 *   $setTableAlign         표 정렬(왼쪽·가운데·오른쪽)을 정한다(TableAlignFloat 가 부른다)
 *   installTableExportPatch  인쇄·시험지로 가는 HTML 에도 같은 계획을 입힌다(PlaygroundNodes 가 한 번 설치)
 */
import {
  $isTableCellNode,
  $isTableRowNode,
  TableCellNode,
  TableNode,
  TableRowNode,
} from '@lexical/table';
import { mergeRegister } from '@lexical/utils';
import { $nodesOfType, LexicalEditor } from 'lexical';

import {
  applyTableAlign,
  applyTableWidthPlan,
  columnWidthsFromCells,
  isCellWidth,
  isSizedTable,
  mapTableGrid,
  planTableWidths,
  roundWidth,
  SizedCell,
  TableAlign,
  tableAlignOf,
  tableCellElements,
} from './tableWidth';

export type TableCellEntry = SizedCell & { node: TableCellNode };

export function $readTableCells(tableNode: TableNode): TableCellEntry[][] {
  return tableNode.getChildren().filter($isTableRowNode).map((row) =>
    row.getChildren().filter($isTableCellNode).map((cell) => ({
      node: cell,
      colSpan: cell.getColSpan(),
      rowSpan: cell.getRowSpan(),
      width: cell.getWidth(),
    })));
}

/** 편집기 안 모든 표에 폭 계획을 입힌다. 표가 몇 개 안 되고 같은 값이면 쓰지 않으므로 매번 전부 돈다 */
export function $styleEditorTables(editor: LexicalEditor): void {
  $nodesOfType(TableNode).forEach((tableNode) => {
    const tableEl = editor.getElementByKey(tableNode.getKey());
    if (!tableEl) return;
    const rows = $readTableCells(tableNode);
    const cellEls = rows.map((cells) => cells.map((c) => editor.getElementByKey(c.node.getKey())));
    applyTableWidthPlan(tableEl, cellEls, planTableWidths(rows), rows.map((cells) => cells.map((c) => c.width)));
    applyTableAlign(tableEl, tableAlignOf(tableNode.getFormatType()));
  });
}

/** 표 정렬을 정한다 — 왼쪽(기본)은 정렬 칸을 비워 둔다(정렬을 안 정한 표와 같은 데이터) */
export function $setTableAlign(tableNode: TableNode, align: TableAlign): void {
  const next = align === 'left' ? '' : align;
  if (tableNode.getFormatType() !== next) tableNode.setFormat(next);
}

/**
 * 편집기에 표 폭 입히기를 건다(TableWidthPlugin 이 부른다). 지금 그려진 표에 한 번 입히고, 표·줄·칸이 바뀔
 * 때마다 다시 입힌다. 돌려주는 함수로 푼다.
 * ⚠ 세 등록에 **서로 다른 함수**를 넘긴다 — Lexical 0.11 은 mutation listener 를 함수를 열쇠로 한 Map 에
 *   담아(`mutations.set(listener, klass)`) 같은 함수를 세 번 넘기면 마지막(칸)만 남는다. 그러면 줄 순서만
 *   바뀐 경우(칸은 그대로)를 놓친다(2차 검토 지적 2026-10-06).
 */
export function registerTableWidthStyling(editor: LexicalEditor): () => void {
  // 표 노드를 등록하지 않은 편집기에서는 mutation 등록이 던진다 — 그런 편집기에는 표도 없다
  if (!editor.hasNodes([TableNode, TableRowNode, TableCellNode])) return () => {};
  const restyle = () => editor.getEditorState().read(() => $styleEditorTables(editor));
  restyle(); // 이 등록보다 먼저 그려진 첫 화면
  return mergeRegister(
    editor.registerMutationListener(TableNode, () => restyle()),
    editor.registerMutationListener(TableRowNode, () => restyle()),
    editor.registerMutationListener(TableCellNode, () => restyle()),
  );
}

/**
 * 크기가 정해진 표에 폭 없는 칸이 생겼을 때(열 넣기 — Lexical 의 $insertTableColumn 은 새 칸에 폭을
 * 주지 않는다) 채운다. 안 채우면 첫 줄에 폭 없는 칸이 생겨 표가 갑자기 꽉 찬 폭으로 돌아간다.
 * 같은 열에 폭을 가진 칸이 있으면 그 폭을, 없으면 fallbackWidth 를 준다.
 */
export function $fillMissingCellWidths(tableNode: TableNode, fallbackWidth: number): void {
  if (!(fallbackWidth > 0)) return;
  const rows = $readTableCells(tableNode);
  const grid = mapTableGrid(rows);
  const known: (number | null)[] = Array(grid.colCount).fill(null);
  rows.forEach((cells, r) => cells.forEach((c, i) => {
    if (c.colSpan <= 1 && isCellWidth(c.width) && known[grid.starts[r][i]] == null) known[grid.starts[r][i]] = c.width;
  }));
  rows.forEach((cells, r) => cells.forEach((c, i) => {
    if (isCellWidth(c.width)) return;
    const start = grid.starts[r][i];
    let w = 0;
    for (let k = 0; k < Math.max(1, c.colSpan); k += 1) w += known[start + k] ?? fallbackWidth;
    c.node.setWidth(roundWidth(w));
  }));
}

/** 크기가 정해진 표면 tableCellNode 가 시작하는 열의 폭, 아니면 null — 열 넣기가 새 열 폭으로 쓴다 */
export function $sizedColumnWidthAt(tableNode: TableNode, cellNode: TableCellNode): number | null {
  const rows = $readTableCells(tableNode);
  if (!isSizedTable(rows)) return null;
  const grid = mapTableGrid(rows);
  const cols = columnWidthsFromCells(rows, grid);
  let at = -1;
  rows.forEach((cells, r) => cells.forEach((c, i) => { if (c.node.is(cellNode)) at = grid.starts[r][i]; }));
  if (cols && at >= 0) return cols[at];
  const widths = rows[0].map((c) => (isCellWidth(c.width) ? c.width : 0));
  return widths.reduce((s, w) => s + w, 0) / Math.max(1, grid.colCount);
}

/**
 * 표를 HTML 로 내보낼 때(인쇄 v4·시험지·미리보기·옛 PDF·복사 — 26곳 모두 $generateHtmlFromNodes)
 * 크기가 정해진 표에 편집기와 같은 비율을 입힌다. Lexical 0.11 에는 내보내기를 바꿀 설정이 없어
 * TableNode.prototype.exportDOM 을 감싼다(PlaygroundNodes 가 모듈을 읽을 때 설치한다).
 *
 * ⚠ Lexical 을 올리면 이 감싸기부터 확인할 것 — 0.11 의 exportDOM 은 `after` 에서 새 <table> 을 만들어
 *   colgroup·tbody 로 옮겨 담는다. tableWidthLexical.test.js 가 그 모양을 지킨다.
 * 크기가 정해지지 않은 표, 그리고 칸 수가 노드와 다른 내보내기(복사할 때 일부 칸만 고른 경우)는
 * **한 글자도 바꾸지 않는다.**
 */
/**
 * 원본 함수를 한 번만 보관하고, 설치할 때마다 **원본 위에 다시** 감싼다. 「이미 설치됨」 표시만 보면
 * 개발 서버가 이 모듈을 바꿔 끼워도(HMR) 옛 감싸기가 남아, 편집기는 새 규칙인데 인쇄·미리보기·PDF 는
 * 옛 규칙으로 갈린다(2026-10-06 오너 보고 「출력·미리보기에서 가운데 정렬이 안 된다」 — 새로 연 페이지는 정상).
 * 원본 위에 감싸므로 몇 번을 불러도 한 겹이다.
 */
const ORIGINAL_EXPORT = '__kyOriginalTableExportDOM';

export function installTableExportPatch(): void {
  const proto = TableNode.prototype as TableNode & {
    [ORIGINAL_EXPORT]?: TableNode['exportDOM'];
  };
  const original = proto[ORIGINAL_EXPORT] ?? proto.exportDOM;
  proto[ORIGINAL_EXPORT] = original;
  proto.exportDOM = function exportDOMWithWidths(this: TableNode, editor: LexicalEditor) {
    const output = original.call(this, editor);
    const tableNode = this;
    const after = output.after;
    return {
      ...output,
      after: (element: HTMLElement | null | undefined) => {
        const result = after ? after.call(tableNode, element) : element;
        const target = (result || element) as HTMLElement | null;
        if (target) {
          try {
            const rows = $readTableCells(tableNode);
            const cellEls = tableCellElements(target);
            const sameShape = cellEls.length === rows.length
              && cellEls.every((cells, r) => cells.length === rows[r].length);
            if (sameShape) applyTableWidthPlan(target, cellEls, planTableWidths(rows));
            // 정렬을 정한 표만 여백을 입힌다 — 안 정한 표의 HTML 은 그대로
            const align = tableAlignOf(tableNode.getFormatType());
            if (align !== 'left') applyTableAlign(target, align);
          } catch (_) {
            // 폭을 못 입혀도 표는 종전 모양으로 나간다 — 인쇄 한 장을 통째로 깨뜨리지 않는다
          }
        }
        return result;
      },
    };
  };
}

/**
 * 저장된 표의 정렬 칸(format)을 다시 읽을 때 되살린다. Lexical 0.11 의 TableNode.importJSON 은 저장된 값을
 * 보지 않고 빈 표를 만든다(문단은 format·indent·direction 을 되살린다) — 그래서 표 정렬이 저장은 되는데
 * 다시 열면 왼쪽으로 돌아갔다(2026-10-06 실측: [보기]·미리보기·종이 모두 왼쪽).
 * **정렬만** 되살린다. 들여쓰기는 지금까지 표에 그려진 적이 없어, 되살리면 옛 표가 밀려 보인다.
 */
const ORIGINAL_IMPORT = '__kyOriginalTableImportJSON';
const KNOWN_FORMATS = new Set(['left', 'start', 'center', 'right', 'end', 'justify']);

export function installTableImportPatch(): void {
  const klass = TableNode as typeof TableNode & {
    [ORIGINAL_IMPORT]?: typeof TableNode.importJSON;
  };
  // 내보내기와 같은 이유로 원본 위에 다시 감싼다(위 ORIGINAL_EXPORT)
  const original = klass[ORIGINAL_IMPORT] ?? klass.importJSON;
  klass[ORIGINAL_IMPORT] = original;
  klass.importJSON = function importJSONWithFormat(
    serialized: Parameters<typeof TableNode.importJSON>[0]
  ): TableNode {
    const node = original.call(this, serialized);
    const format = serialized ? serialized.format : undefined;
    // Lexical 이 아는 값만 — 모르는 문자열은 setFormat 이 정렬 칸에 undefined 를 넣는다
    if (typeof format === 'string' && KNOWN_FORMATS.has(format)) node.setFormat(format);
    return node;
  };
}

/** 표 노드 덧씌우기 둘(내보내기 폭·정렬 / 읽기 정렬) — PlaygroundNodes 가 모듈을 읽을 때 한 번 */
export function installTableNodePatches(): void {
  installTableExportPatch();
  installTableImportPatch();
}
